// A simulation for a lens other than actions: the action-level simulation runs as it does on the Actions view, and its
// results are rolled up to the lens's nodes. A node's queue is the work waiting at its actions, and an edge sends a
// packet when items pass between the actions it stands for. One engine, so the numbers match across lenses
import { isCanvasOnPage, removeFromRenderTree } from '../canvas/render-tree';
import { Animator, GraphComponent, IEdge, INode, IRenderTreeElement, SvgVisual, TimeSpan } from '@yfiles/yfiles';
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { createPacketElement, PacketAnimation, svgns } from '../canvas/styles/edge-style';
import { LABEL_TYPES } from '../model-services/model-item-enums';
import { TemporalLabelStyleDecorator } from '../temporal/temporal-label-style-decorator';
import { TemporalNode } from '../temporal/temporal-model';
import { WORKING_SECONDS_PER_DAY } from '../temporal/simulation-report';
import { SimulationRun } from '../temporal/simulation-run';
import { LensNodeTag } from './lens-node-style';

// as on the Actions view: six minutes of work a tick, five ticks a second, and the graph redrawn a few times a second
const SECONDS_PER_TICK = 360;
const MILLISECONDS_PER_TICK = 200;
const MILLISECONDS_BETWEEN_DRAWS = 400;
const PACKET_COLOUR = '#0f8b8d';

// what a lens node and edge stand for, in actions
export interface LensSimulationMap {
    nodes: { key: string; actionKeys: string[] }[];
    edges: { key: string; actionPairs: [string, string][] }[];
}

export interface LensSimulationProgress {
    week: number;
    day: number;
    received: number;
    produced: number;
    waiting: number;
    finished: boolean;
}

export class LensSimulation {
    running = false;
    private run?: SimulationRun;
    private timer?: number;
    private lastDrawn = 0;
    private animator?: Animator;
    private packetElement?: IRenderTreeElement;
    private readonly labelStyle = new TemporalLabelStyleDecorator();
    private actions = new Map<string, TemporalNode>();
    private nodes: { node: INode; actions: TemporalNode[] }[] = [];
    private edges: { edge: IEdge; pairs: { from: TemporalNode; toKey: string }[]; packet?: PacketAnimation }[] = [];

    constructor(private graphComponent: GraphComponent, private onTick: () => void) {}

    get started() {
        return !!this.run;
    }

    get finished() {
        return !!this.run?.finished;
    }

    // a new run from the start, over the actions in the models given
    start(analysis: { nodes: AnalysisNode[]; edges: AnalysisEdge[] }, modelKeys: Set<string>, map: LensSimulationMap, nodeFor: (key: string) => INode | undefined,
        edgeFor: (key: string) => IEdge | undefined) {
        this.clear();
        this.run = SimulationRun.fromAnalysis(analysis, modelKeys, SECONDS_PER_TICK);
        this.actions = new Map(this.run.nodes.map(n => [n.node._key, n.node]));
        const graph = this.graphComponent.graph;

        this.nodes = map.nodes.map(n => ({ node: nodeFor(n.key)!, actions: n.actionKeys.map(k => this.actions.get(k)).filter((a): a is TemporalNode => !!a) }))
            .filter(n => !!n.node);
        this.nodes.forEach(n => {
            (n.node.tag as LensNodeTag).temporalNode = { queue: 0 };
            graph.addLabel({ owner: n.node, text: '', style: this.labelStyle, tag: { type: LABEL_TYPES.QUEUE } });
        });

        // packets drawn above the graph, one for each edge
        this.animator = new Animator({ canvasComponent: this.graphComponent, allowUserInteraction: true });
        this.animator.autoInvalidation = false;
        void this.animator.animate(() => {}, TimeSpan.MAX_VALUE);
        const g = window.document.createElementNS(svgns, 'g');
        this.edges = map.edges.map(e => ({ edge: edgeFor(e.key)!, pairs: e.actionPairs.map(([from, to]) => ({ from: this.actions.get(from)!, toKey: to })).filter(p => !!p.from) }))
            .filter(e => !!e.edge)
            .map(e => ({ ...e, packet: new PacketAnimation(createPacketElement(g, PACKET_COLOUR), e.edge) }));
        this.packetElement = this.graphComponent.renderTree.createElement(this.graphComponent.renderTree.contentGroup, new SvgVisual(g));
        this.continue();
    }

    continue() {
        if (!this.run || this.running || this.run.finished) return;
        this.running = true;
        this.schedule();
    }

    stop() {
        this.running = false;
        window.clearTimeout(this.timer);
        this.timer = undefined;
        this.graphComponent.invalidate();
    }

    clear() {
        this.stop();
        this.animator?.stop();
        // a canvas whose view has gone has been cleaned up: its graph can't be tidied, and needn't be
        if (isCanvasOnPage(this.graphComponent)) {
            removeFromRenderTree(this.graphComponent, this.packetElement);
            const graph = this.graphComponent.graph;
            this.nodes.forEach(n => {
                n.node.labels.filter(l => l.tag?.type === LABEL_TYPES.QUEUE).toArray().forEach(l => graph.remove(l));
                delete (n.node.tag as LensNodeTag).temporalNode;
            });
            this.graphComponent.invalidate();
        }
        this.packetElement = undefined;
        this.nodes = [];
        this.edges = [];
        this.run = undefined;
    }

    // the work waiting at each of the lens's nodes, most first
    queues(): { key: string; queue: number }[] {
        return this.nodes.map(n => ({ key: (n.node.tag as LensNodeTag).lens.key, queue: (n.node.tag as LensNodeTag).temporalNode?.queue || 0 }))
            .filter(q => q.queue > 0).sort((a, b) => b.queue - a.queue);
    }

    progress(): LensSimulationProgress | undefined {
        if (!this.run) return undefined;
        const snapshot = this.run.snapshot();
        const day = Math.floor(Math.max(0, snapshot.tick - 1) * SECONDS_PER_TICK / WORKING_SECONDS_PER_DAY);
        return {
            week: Math.floor(day / 5) + 1, day: day % 5 + 1, received: Math.round(snapshot.received),
            produced: snapshot.outputs.reduce((sum, o) => sum + o.produced, 0),
            waiting: snapshot.steps.reduce((sum, s) => sum + s.queue, 0), finished: snapshot.finished,
        };
    }

    private schedule() {
        this.timer = window.setTimeout(() => {
            this.timer = undefined;
            if (!this.running || !this.run || this.run.finished) {
                this.running = false;
                this.onTick();
                return;
            }
            this.tick();
            this.schedule();
        }, MILLISECONDS_PER_TICK);
    }

    private tick() {
        const run = this.run!;
        run.tick();
        run.tickNumber++;
        this.nodes.forEach(n => (n.node.tag as LensNodeTag).temporalNode = { queue: n.actions.reduce((sum, a) => sum + a.queue, 0) });
        // a packet along each hand-over that passed items this tick
        this.edges.forEach(e => {
            if (e.packet && e.pairs.some(p => p.from.next.some(x => x.node._key === p.toKey && x.itemsSent > 0))) e.packet.sendPacket(this.animator!);
        });
        const now = Date.now();
        if (now - this.lastDrawn >= MILLISECONDS_BETWEEN_DRAWS) {
            this.lastDrawn = now;
            this.graphComponent.updateVisual();
            this.onTick();
        }
    }
}
